import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
  AuctionItemTenant,
  BackLinkState,
  BidFilter,
  ClearAllSelectedVehicles,
  ClearOfferListUuids,
  ClearUserUuids,
  ClearVinList,
  CloseDrawer,
  ConfigurationState,
  DateUtilsService,
  DealerFilter,
  DealerToRecipient,
  DoSearch,
  GroundedStatusFilters,
  LaunchDarklyService,
  MetaDataOfferList,
  OfferListMeta,
  RecipientOfferLists,
  RemoveFilter,
  ResetDateFilters,
  ResetFilters,
  ResetMultiVin,
  SearchAuctionItems,
  SearchFilterQueries,
  SearchFiltersState,
  searchParamsFromSellSort,
  SellConfigurationState,
  SellSearchState,
  SetBidFilter,
  SetCurrentUserAsOfferListCreatorOnLoad,
  SetDealerFilterSellSearch,
  SetDefaultSortForStatus,
  SetGroundedStatusFilter,
  SetOfferFilter,
  SetQuestionFilter,
  SetReservedByUsers,
  SetReservedForDealers,
  SetSelectedTenant,
  SetSingleListSelected,
  SetStatusFilter,
  StatusFilter,
  ToggleDealerOffers,
  ToggleDealerQuestions,
  ToggleLostDealer,
  ToggleSelectAllLostDealers,
  ToggleSelectAllOffers,
  ToggleSelectAllQuestions,
  ToggleSelectAllUsers,
  ToggleSelectAllWonDealers,
  ToggleUsers,
  ToggleWonDealer,
  UpdatedOfferLists,
  UpdateFilterByInServicePayload,
  UpdateFilterStatus,
  UpdateLeadSellerPayload,
  UpdateReservedByUsers,
  UpdateReservedForDealers,
  UpdateReturnToInventory,
  UpdateShouldMaintainPreviousSearchInState
} from 'remarketing-angular-library';
import {
  SelectableDealer
} from 'remarketing-angular-library/states/search-filter/interfaces/selectable-dealer.interface';
import { filter, skipWhile, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { SearchFiltersService } from '../../services/search-filters.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LeadSellerGroupService } from '../../services/lead-seller-group/lead-seller-group.service';
import { selectedGroupMemberUuids, selectedGroupUuid } from '../../services/lead-seller-group/reducer';
import { BsoUnsavedChangesService } from '../../../sell-shared/services/bso-unsaved-changes.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { cloneDeep } from 'lodash';
import { SelectableUser } from 'remarketing-angular-library/states/search-filter/interfaces/selectable-user.interface';
import { AnalyticsService } from 'smartauction-frontend-micro-library';
import {
  VehicleTypeSelectComponent
} from '../../../../../shared/components/vehicle-type-select/vehicle-type-select.component';
import { AvisVehicleType } from '../../../../../app/enums/vehicleInfo.enum';
import {
  DropdownSelectComponent,
  RadioButtonGroupComponent,
  TrapFocusComponent
} from 'remarketing-ui-components-lib';
import {
  UtilityBarDateRangeComponent
} from '../utility-bar/utility-bar-date-range/utility-bar-date-range.component';
import {
  SearchFilterSectionComponent
} from './search-filter-section/search-filter-section.component';
import { CommonModule } from '@angular/common';
import { DealerFilterComponent } from '../dealer-filter/dealer-filter.component';

@Component({
    selector: 'lib-search-filter',
    templateUrl: './search-filter.component.html',
    styleUrls: ['./search-filter.component.scss'],
    imports: [
        VehicleTypeSelectComponent,
        TranslocoDirective,
        RadioButtonGroupComponent,
        UtilityBarDateRangeComponent,
        SearchFilterSectionComponent,
        CommonModule,
        DealerFilterComponent,
        TrapFocusComponent,
        DropdownSelectComponent
    ]
})
export class SearchFilterComponent implements OnInit, OnDestroy {
  @Select(SearchFilterQueries.statusFilters) readonly statusFilter$: Observable<StatusFilter>;
  @Select(SearchFilterQueries.selectedStatusFilter) readonly selectedStatusFilter$: Observable<StatusFilter>;
  @ViewChild('selectAllOffers') selectAllOffersCheckbox: ElementRef<HTMLInputElement>;
  @ViewChild('selectAllUsers') selectAllUsersCheckbox: ElementRef<HTMLInputElement>;
  @ViewChild('selectAllQuestions') selectAllQuestionsCheckbox: ElementRef<HTMLInputElement>;
  @ViewChild('selectAllRecipients') selectAllRecipientsCheckbox: ElementRef<HTMLInputElement>;
  @ViewChild('vehicleTypeSelectComponent') vehicleTypeSelectComponent: VehicleTypeSelectComponent;
  @Select(SearchFiltersState.getSelectedGroundedFilter) groundedStatusFilter$: Observable<GroundedStatusFilters>;
  @Input() isMobile: boolean;
  offerFiltersState$ = this.searchFiltersService.offerFiltersState$;
  filterByIsv: boolean;
  sharedListFilterState$ = this.searchFiltersService.sharedFiltersState$;
  userFilterState$ = this.searchFiltersService.userFiltersState$;
  questionFiltersState$ = this.searchFiltersService.questionFiltersState$;
  paramsHandled = false;
  filterByStatus = StatusFilter.ACTIVE_VEHICLES;
  filterByStatusSelected = StatusFilter.ACTIVE_VEHICLES;
  filterByRepostedSelected = RemoveFilter.ALL;
  selectedBidsOffers: DealerFilter = DealerFilter.OFFERS_NONE;
  wonDealersState$ = this.searchFiltersService.wonDealers$;
  lostDealersState$ = this.searchFiltersService.lostDealers$;
  selectedMemberUuid$ = this.leadSellerGroupService.select(selectedGroupMemberUuids);
  selectedGroupUuid$ = this.leadSellerGroupService.select(selectedGroupUuid);
  selectedMemberUuid: string[];
  selectedGroupUuid: string[];
  tenantSelected = 'ALL';
  paramsInUrl = false;
  sellerDealerName: string;
  selectedUsers: string[] = [];
  userFilterValue: 'NONE' | 'ALL' | 'OUTSTANDING' = 'NONE';
  isLeadSeller: string[];
  analyticsRadioOption: string;
  groundedSectionOpen = true; //keep grounded status section expanded by default
  reservedByUserValue: string | null;
  dealershipValue: string | null;

  searchFilterTranslations = {
    labelReserveInformation: '',
    labelAvisRep: '',
    labelDealership: '',
    labelSoldDate: '',
    sharedVehicles: '',
    customOfferVehicles: '',
    labelSharedVehiclesFilter: '',
    labelAllSharedListOffersTo: '',
    labelAllCustomOffersTo: '',
    labelAllOffersTo: ''
  };

  leadSellerRequestMade$ = this.leadSellerGroupService.leadSellerResponse$;
  bids = [
    {
      id: 'active-filter-all-bids',
      class: '',
      groupName: 'Bids',
      name: '',
      value: BidFilter.ALL,
      dataTrackName: 'active-filter-all-bids',
      ariaLabel: '',
      selected: 'activeFilterByTypes.all',
      isChecked: true
    },
    {
      id: 'active-filter-active-bids',
      class: '',
      groupName: 'Bids',
      name: '',
      value: BidFilter.WITH_BIDS,
      dataTrackName: 'active-filter-active-bids',
      ariaLabel: '',
      selected: 'activeFilterByTypes.activebids',
      isChecked: false
    },
    {
      id: 'active-filter-no-bids',
      class: '',
      groupName: 'Bids',
      name: '',
      value: BidFilter.WITHOUT_BIDS,
      dataTrackName: 'active-filter-no-bids',
      ariaLabel: '',
      selected: 'activeFilterByTypes.nobids',
      isChecked: false
    }
  ];

  defaultRadioButton = {
    id: 'active-tenant-filter',
    class: 'custom-control-input',
    groupName: 'Tenant',
    name: 'All',
    value: 'ALL',
    dataTrackName: 'Active-All',
    ariaLabel: 'All',
    isChecked: true
  };

  tenants = [this.defaultRadioButton];

  offerFilterTypes = {
    none: DealerFilter.OFFERS_NONE,
    withOffers: DealerFilter.WITH_OFFERS,
    byDealer: DealerFilter.OFFERS_BY_DEALER
  };

  questionFilterTypes = {
    none: DealerFilter.QUESTIONS_NONE,
    withQuestions: DealerFilter.WITH_QUESTIONS,
    byDealer: DealerFilter.QUESTIONS_BY_DEALER
  };

  statusFilterTypes = {
    active: StatusFilter.ACTIVE_VEHICLES,
    sold: StatusFilter.SOLD_VEHICLES,
    reserved: StatusFilter.RESERVED_VEHICLES,
    all: StatusFilter.ALL_VEHICLES,
    shared: StatusFilter.SHARED_VEHICLES,
    hold: StatusFilter.HOLD_VEHICLES,
    removed: StatusFilter.REMOVED_VEHICLES
  };

  removeFilterByTypes = {
    all: RemoveFilter.ALL,
    not_reposted: RemoveFilter.NOT_REPOSTED,
    reposted: RemoveFilter.REPOSTED
  };

  dealerFilters = {
    all: undefined,
    wonByDealer: DealerFilter.WON_BY_DEALER,
    bidsOffersByDealer: DealerFilter.BIDS_OFFERS_BY_DEALER
  };

  reservedByUsersDropdown;
  dealershipDropdown;
  groundedFilterByTypes = {
    all: GroundedStatusFilters.ALL,
    not_grounded: GroundedStatusFilters.NOT_GROUNDED,
    grounded: GroundedStatusFilters.GROUNDED
  };

  private destroyed$ = new Subject();

  modalId = 'bso-alert-modal-filter';
  allRecipientSelected = true;
  openSharedListDealerShip = [];
  allListTranslate: string;
  selectedGroundedOptions: GroundedStatusFilters = GroundedStatusFilters.ALL;

  get isReserveFilterEnabled(): boolean {
    return (
      this.isSellReservationFiltersEnabled &&
      (this.filterByStatusSelected === 'RESERVED' || this.filterByStatusSelected === 'SOLD') &&
      this.filterByIsv
    );
  }

  constructor(
    readonly store: Store,
    readonly searchFiltersService: SearchFiltersService,
    private route: ActivatedRoute,
    private router: Router,
    private leadSellerGroupService: LeadSellerGroupService,
    private readonly bsoUnsavedChangesService: BsoUnsavedChangesService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly translocoService: TranslocoService,
    private readonly analyticsService: AnalyticsService,
    private readonly dateUtilsService: DateUtilsService
  ) {}

  ngOnInit(): void {
    this.setAnalyticsVariables();
    this.store.dispatch(new UpdateShouldMaintainPreviousSearchInState(false));
    this.getReservedFromDealersValueFromState();
    this.getReservedByUsersValueFromState();
    if (!this.paramfilterByDealer) {
      this.setIsv();
    }
    this.groundedStatusFilter$.pipe(takeUntil(this.destroyed$)).subscribe(groundedStatusFilter => {
      this.selectedGroundedOptions = groundedStatusFilter;
    });

    this.isLeadSeller = this.store.selectSnapshot(_state =>
      _state.user.userProfile.fineGrainedRoles.includes('Lead_Seller')
    );
    const state = this.store.selectSnapshot(BackLinkState.backLinkState);
    const fromLink = state.from;
    if (
      fromLink &&
      fromLink.indexOf('/inventory') === -1 &&
      (fromLink.indexOf('/details') === -1 ||
        fromLink.indexOf('/offers') === -1 ||
        fromLink.indexOf('/questions') !== -1)
    ) {
      this.selectedGroundedOptions = GroundedStatusFilters.ALL;
    }
    if (this.isLeadSeller) {
      combineLatest([this.selectedMemberUuid$, this.selectedGroupUuid$])
        .pipe(takeUntil(this.destroyed$))
        .subscribe(([selectedMemberUuids, selectedGroupUuids]) => {
          this.selectedMemberUuid = selectedMemberUuids;
          this.selectedGroupUuid = selectedGroupUuids;
          this.handleParams();
          this.loadLeadSellerGroupAndHandleParam();
          this.store.dispatch([
            new UpdateFilterByInServicePayload(this.filterByIsv),
            new UpdateLeadSellerPayload(this.selectedMemberUuid)
          ]);
        });
    } else {
      this.handleParams();
      this.loadLeadSellerGroupAndHandleParam();
    }
    this.getTranslate();
    this.store
      .select(storeState => storeState.org)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(data => {
        if (data && data.organization) {
          this.sellerDealerName = data.organization.oeName;
        }
      });

    this.store
      .select(SearchFiltersState.getBidFilter)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(bidFilter => {
        if (!bidFilter) {
          bidFilter = BidFilter.ALL;
        }

        this.bids.forEach(bid => {
          bid.isChecked = bid.value === bidFilter;
        });
      });

    this.setTenantRadioButtons();

    this.store.dispatch(new SetStatusFilter(this.store.selectSnapshot(SellConfigurationState.filterStatus)));

    this.statusFilter$.pipe(takeUntil(this.destroyed$)).subscribe(statusFilter => {
      this.filterByStatus = statusFilter;
    });

    this.selectedStatusFilter$.pipe(takeUntil(this.destroyed$)).subscribe(statusFilter => {
      this.filterByStatusSelected = statusFilter;
    });

    this.searchFiltersService.offerFilters$
      .pipe(
        skipWhile(f => !f),
        takeUntil(this.destroyed$)
      )
      .subscribe(({ selectAllChecked }) => {
        if (this.selectAllOffersCheckbox && this.selectAllOffersCheckbox.nativeElement.checked !== selectAllChecked) {
          this.selectAllOffersCheckbox.nativeElement.checked = selectAllChecked;
        }
      });

    this.searchFiltersService.userFilters$
      .pipe(
        skipWhile(f => !f),
        takeUntil(this.destroyed$)
      )
      .subscribe(({ selectAllChecked }) => {
        if (this.selectAllUsersCheckbox && this.selectAllUsersCheckbox.nativeElement.checked !== selectAllChecked) {
          this.selectAllUsersCheckbox.nativeElement.checked = selectAllChecked;
        }
      });

    this.searchFiltersService.questionFilters$
      .pipe(
        skipWhile(f => !f),
        takeUntil(this.destroyed$)
      )
      .subscribe(({ selectAllChecked }) => {
        if (
          this.selectAllQuestionsCheckbox &&
          this.selectAllQuestionsCheckbox.nativeElement.checked !== selectAllChecked
        ) {
          this.selectAllQuestionsCheckbox.nativeElement.checked = selectAllChecked;
        }
      });

    this.searchFiltersService.dealerFilter$.pipe(takeUntil(this.destroyed$)).subscribe(val => {
      this.selectedBidsOffers = val;
    });
    this.getSoldReservedByUsersOptions();
    this.getSoldDealershipDropdownOptions();
  }

  getReservedByUsersValueFromState() {
    this.store
      .select(SellConfigurationState.reservedByUsers)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.reservedByUserValue = response;
      });
  }

  getReservedFromDealersValueFromState() {
    this.store
      .select(SellConfigurationState.reservedForDealers)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.dealershipValue = response;
      });
  }

  setAnalyticsVariables(): void {
    this.analyticsRadioOption = this.isInServiceVehiclesEnabled ? 'Invoiced' : 'Shared';
  }

  getSoldReservedByUsersOptions() {
    this.store
      .select(SellSearchState.searchResponse)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        const reservedByUsers = response?.metadata.reservedByUsers;
        if (reservedByUsers?.length > 0) {
          this.reservedByUsersDropdown = [
            {
              options: [
                {
                  name: 'All',
                  value: 'All',
                  selected: this.reservedByUserValue === null
                },
                ...reservedByUsers.map((value: any) => {
                  return {
                    name: value.userName,
                    value: value.userUuid,
                    selected: this.reservedByUserValue === value.userUuid
                  };
                })
              ]
            }
          ];
        } else {
          this.reservedByUsersDropdown = [
            {
              options: [
                {
                  name: 'All',
                  value: 'All',
                  selected: true
                }
              ]
            }
          ];
        }
      });
  }

  getSoldDealershipDropdownOptions() {
    this.store
      .select(SellSearchState.searchResponse)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        const reservedForDealers = response?.metadata.reservedForDealers;
        if (reservedForDealers?.length > 0) {
          this.dealershipDropdown = [
            {
              options: [
                {
                  name: 'All',
                  value: 'All',
                  selected: this.dealershipValue === null
                },
                ...reservedForDealers.map((value: any) => {
                  return {
                    name: value.name,
                    value: value.organizationEntityUuid,
                    selected: this.dealershipValue === value.organizationEntityUuid
                  };
                })
              ]
            }
          ];
        } else {
          this.dealershipDropdown = [
            {
              options: [
                {
                  name: 'All',
                  value: 'All',
                  selected: true
                }
              ]
            }
          ];
        }
      });
  }

  get showTenantFilter() {
    return (
      this.isAdmin ||
      (!this.filterByIsv &&
        this.isSellRemovalTypeFilterEnabled() &&
        (this.isSoldVehiclesFilterSelected || this.isActiveVehiclesFilterSelected))
    );
  }

  get isAdmin(): boolean {
    return this.store.selectSnapshot(ConfigurationState.isAdmin());
  }

  get paramfilterByDealer(): string {
    return this.route.snapshot.queryParams.filterByDealer;
  }

  get paramfilterByStatus(): StatusFilter {
    return this.route.snapshot?.queryParams?.filterByStatus
      ? this.statusFilterTypes[this.route.snapshot?.queryParams?.filterByStatus?.toLowerCase()]
      : undefined;
  }

  get paramFilterByUser(): boolean {
    return this.route.snapshot.queryParams.filterByUser;
  }

  get isSoldInventoryFeatureEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-soldInventoryOptionEnabled', false);
  }

  get isSellUpdateInventoryFeatureEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
  }

  get isSellReservationFiltersEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-reservationFiltersEnabled', false);
  }

  get isInServiceVehicleSelected(): boolean {
    return this.filterByIsv;
  }

  get isOfferListFeatureEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('offerList-seller-createEnabled', false);
  }

  get isReservedFilterEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sim-reserved-filterEnabled', false);
  }

  get isInServiceVehiclesEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get isSellHoldFilterEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-holdFilterEnabled', false);
  }

  get isSellRemovedFilterEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-removedFilterEnabled', false);
  }

  get isSellAllFilterEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-allFilterEnabled', false);
  }

  get isCustomOffersEnabled() {
    return this._launchDarklyService.getFeatureFlag('offerlist-avisCustomOfferEnabled', false);
  }

  get isSharedVehicleFilterEnabled() {
    if (this.isInServiceVehiclesEnabled && !this.filterByIsv) {
      return this.isOfferListFeatureEnabled && this.isCustomOffersEnabled;
    }
    return this.isOfferListFeatureEnabled;
  }

  setIsv() {
    this._launchDarklyService
      .waitForLaunchDarkly()
      .pipe(take(1))
      .subscribe(ready => {
        if (ready) {
          if (this.isInServiceVehiclesEnabled) {
            const filterByInService: boolean = this.route.snapshot.queryParams.filterByInService
            ? (this.route.snapshot.queryParams.filterByInService === 'true')
            :
            this.store.selectSnapshot(SellSearchState.searchRequest)?.filterByInService;
            if (filterByInService == undefined) {
              this.filterByIsv = true;
            } else {
              this.filterByIsv = filterByInService;
            }
          } else {
            this.filterByIsv = false;
          }
          this.store.dispatch(new UpdateFilterByInServicePayload(this.filterByIsv));
        }
      });
  }

  get removalFilterByAll(): boolean {
    return this.filterByRepostedSelected === this.removeFilterByTypes.all;
  }

  get removalFilterByNotReposted(): boolean {
    return this.filterByRepostedSelected === this.removeFilterByTypes.not_reposted;
  }

  get removalFilterByReposted(): boolean {
    return this.filterByRepostedSelected === this.removeFilterByTypes.reposted;
  }

  get isRemovalFilterHideShow(): boolean {
    return this.filterByStatusSelected === 'REMOVED' && this.removedVehiclesSA();
  }

  get isActiveVehiclesFilterSelected(): boolean {
    return this.filterByStatusSelected === 'ACTIVE';
  }

  get showBidLeftRailFilters(): boolean {
    return !this.filterByIsv && this._launchDarklyService.getFeatureFlag('sell-bidFilterEnabled', true);
  }

  get showOffersLeftRailFilters(): boolean {
    return !this.filterByIsv && this._launchDarklyService.getFeatureFlag('sell-offerFilterEnabled', true);
  }

  get showQuestionsLeftRailFilters(): boolean {
    return !this.filterByIsv && this._launchDarklyService.getFeatureFlag('sell-questionsFilterEnabled', true);
  }

  get showSoldBidsOffersLeftRailFilters(): boolean {
    return !this.filterByIsv && this._launchDarklyService.getFeatureFlag('sell-bidsOffersFiltersEnabled', true);
  }

  get showRemovalTypeLeftRailFilters(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-removalTypeFilterEnabled', true);
  }

  get isSoldVehiclesFilterSelected(): boolean {
    return this.filterByStatusSelected === 'SOLD';
  }

  get isSharedVehiclesFilterSelected(): boolean {
    return this.filterByStatusSelected === 'SHARED';
  }

  get isGroundingStatusFilterHideShow(): boolean {
    return (
      this.filterByIsv &&
      this._launchDarklyService.getFeatureFlag('sell-groundedFilterEnabled', false) &&
      (this.filterByStatusSelected === StatusFilter.ACTIVE_VEHICLES ||
        this.filterByStatusSelected === StatusFilter.RESERVED_VEHICLES)
    );
  }

  get groundedFilterByAll(): boolean {
    return this.selectedGroundedOptions === this.groundedFilterByTypes.all;
  }

  get groundedFilterByGrounded(): boolean {
    return this.selectedGroundedOptions === this.groundedFilterByTypes.grounded;
  }

  get groundedFilterByNotGrounded(): boolean {
    return this.selectedGroundedOptions === this.groundedFilterByTypes.not_grounded;
  }

  private handleParams(): void {
    if (!this.paramfilterByStatus) {
      this.selectedStatusFilter$.pipe(takeUntil(this.destroyed$)).subscribe(statusFilter => {
        this.filterByStatus = statusFilter;
      });
    } else {
      this.paramsInUrl = true;
      this.filterByStatus = this.paramfilterByStatus;
    }
    if (this.paramFilterByUser) {
      this.handleFilterByUser();
      return;
    }
    this.store.dispatch(new UpdateFilterStatus(this.filterByStatus));
    this.searchFiltersService.setStatusFilter(this.store.selectSnapshot(SellConfigurationState.filterStatus));

    if (this.paramfilterByDealer) {
      const leadSellerGroupMembers = selectedGroupMemberUuids(this.leadSellerGroupService.snapShot());
      this.store.dispatch([new ClearVinList(), new UpdateLeadSellerPayload(leadSellerGroupMembers)]);
      switch (this.paramfilterByDealer) {
        case DealerFilter.WITH_OFFERS:
          if (this.searchFiltersService.currentFilter !== 'offers') {
            this.searchFiltersService.selectFilter('offers');
          }
          this.setOfferFilter(DealerFilter.WITH_OFFERS);
          this.paramsHandled = true;
          break;
        case DealerFilter.WITH_QUESTIONS:
          if (this.searchFiltersService.currentFilter !== 'questions') {
            this.searchFiltersService.selectFilter('questions');
          }
          this.setOfferFilter(DealerFilter.WITH_QUESTIONS);
          this.paramsHandled = true;
          break;
        default:
        // do nothing
      }
    } else if (this.filterByStatus) {
      this.allRecipientSelected = true;
      this.handleFilterByStatus();
    }
    this.clearParams();
  }

  handleFilterByStatus() {
    const existingSearchRequest = this.store.selectSnapshot(SellSearchState.searchRequest);
    this.filterByIsv = existingSearchRequest.filterByInService;
    this.leadSellerRequestMade$.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (value) {
        if (
          this.isExistingInputFieldSearch(existingSearchRequest) ||
          this.isExistingSearch(existingSearchRequest) ||
          (existingSearchRequest.filterByDealer && existingSearchRequest.filterByDealer !== DealerFilter.OFFERS_NONE)
        ) {
          this.store.dispatch(new SearchAuctionItems(existingSearchRequest));
          this.paramsHandled = true;
        } else {
          this.executeDefaultSearch();
          this.paramsHandled = true;
        }
      }
    });
  }

  handleFilterByUser() {
    this.store
      .select(storeState => storeState.user)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(data => {
        if (data && data.userProfile) {
          const userName = data.userProfile.userName;
          const userUuid = data.userProfile.userUuid;
          this.userFilterValue = 'ALL';
          this.allRecipientSelected = false;
          this.filterByStatus = StatusFilter.SHARED_VEHICLES;
          if (this.searchFiltersService.currentFilter !== 'offers') {
            this.searchFiltersService.selectFilter('offers');
          }
          this.store.dispatch([
            new ClearVinList(),
            new SetCurrentUserAsOfferListCreatorOnLoad({
              offerListCreatorUuid: userUuid,
              sellerName: userName
            } as MetaDataOfferList),
            new UpdateFilterStatus(StatusFilter.SHARED_VEHICLES),
            new SetStatusFilter(StatusFilter.SHARED_VEHICLES),
            new SetOfferFilter(DealerFilter.OFFERS_BY_DEALER),
            new DoSearch()
          ]);
          this.paramsHandled = true;
          this.clearParams();
          this.paramsInUrl = false;
        }
      });
  }

  /* Hold, & All should use default search ( no sub filters) -> return false
   * For Shared, Active, Sold & Removed, if sub filter exist -> return true */
  isExistingSearch(existingSearchRequest): boolean {
    if (this.paramsInUrl) {
      return false;
    }

    if (this.isAdmin) {
      if (
        existingSearchRequest.tenantUuid &&
        (existingSearchRequest.tenantUuid !== '' ||
          existingSearchRequest.tenantUuid !== null ||
          existingSearchRequest.tenantUuid !== undefined)
      ) {
        return true;
      }
    }

    const toDate = this.dateUtilsService.DEFAULT_DATES.toDate;
    const fromDate = this.getDefaultFromDate();
    switch (this.filterByStatus) {
      case StatusFilter.ACTIVE_VEHICLES:
        if (existingSearchRequest.filterByBids) {
          return true;
        }
        if (existingSearchRequest.filterByDealer) {
          return true;
        }
        if (existingSearchRequest.tenantUuid) {
          return true;
        }
        break;
      case StatusFilter.SOLD_VEHICLES:
        if (existingSearchRequest.filterByDealer) {
          return true;
        }
        if (existingSearchRequest.tenantUuid) {
          return true;
        }
        // if date exists, compare with default
        if (existingSearchRequest.toDate && existingSearchRequest.toDate !== toDate) {
          return true;
        }
        if (existingSearchRequest.fromDate && existingSearchRequest.fromDate !== fromDate) {
          return true;
        }
        break;
      case StatusFilter.REMOVED_VEHICLES:
        if (existingSearchRequest.filterByReposted !== 'ALL') {
          this.persistRemovalTypeFilter(existingSearchRequest);
          return true;
        }
        // if date exists, compare with default
        if (existingSearchRequest.toDate && existingSearchRequest.toDate !== toDate) {
          return true;
        }
        if (existingSearchRequest.fromDate && existingSearchRequest.fromDate !== fromDate) {
          return true;
        }
        break;
      case StatusFilter.SHARED_VEHICLES:
        if (existingSearchRequest.offerLists) {
          this.maintainSharedListFilter();
          return true;
        } else if (existingSearchRequest.filterByDealer) {
          // Only two dealer filters can be selected in this state.
          this.userFilterValue =
            existingSearchRequest.filterByDealer === DealerFilter.OFFERS_BY_DEALER ? 'ALL' : 'OUTSTANDING';
          this.allRecipientSelected = false;
          this.store.dispatch(new DoSearch());
          return true;
        }
        break;
    }
    return false;
  }

  /* Get params from state & call
   * handleOfferListRadioButton to set shared list display */
  maintainSharedListFilter() {
    let dealerToRecipientLists: DealerToRecipient[]; //list under all recipients
    let recipientOfferLists: RecipientOfferLists; // selected buyer name & offer lists
    let selectedOfferListMeta: OfferListMeta; // selected offer list
    let buyerDealerName: string; // dealer with selected offer list

    this.sharedListFilterState$.pipe(take(1)).subscribe(filterState => {
      dealerToRecipientLists = filterState.sharedList;
      // check each dealer
      for (let i = 0; i < dealerToRecipientLists?.length; i++) {
        const dealerRecipientList = dealerToRecipientLists[i];
        buyerDealerName = dealerRecipientList.buyerDealerName;
        // check each offer list
        for (let j = 0; j < dealerRecipientList.recipientOfferLists.length; j++) {
          recipientOfferLists = dealerRecipientList.recipientOfferLists[j];
          // find selected offer list
          for (let k = 0; k < recipientOfferLists.offerLists.length; k++) {
            const offerList = recipientOfferLists.offerLists[k];
            if (offerList.selected) {
              selectedOfferListMeta = offerList;
              this.handleOfferListRadioButton(
                dealerToRecipientLists,
                recipientOfferLists,
                selectedOfferListMeta,
                buyerDealerName
              );
              this.openDealerShipDropDown(dealerRecipientList);
              return;
            }
          }
        }
      }
    });
  }

  persistRemovalTypeFilter(existingSearchRequest) {
    if (existingSearchRequest.filterByReposted === 'NOT_REPOSTED') {
      this.filterByRepostedSelected = RemoveFilter.NOT_REPOSTED;
    } else if (existingSearchRequest.filterByReposted === 'REPOSTED') {
      this.filterByRepostedSelected = RemoveFilter.REPOSTED;
    }
  }

  isExistingInputFieldSearch(existingSearchRequest) {
    return (
      this.isExistingMultiVinOrMultiStockNumberSearch(existingSearchRequest) ||
      this.isExistingVinOrStockNumberOrLegacySellerIdSearch(existingSearchRequest)
    );
  }

  isExistingVinOrStockNumberOrLegacySellerIdSearch(existingSearchRequest) {
    return (
      existingSearchRequest?.vin?.length > 0 ||
      existingSearchRequest?.stockNumber?.length > 0 ||
      existingSearchRequest?.sellerLegacyId?.length > 0
    );
  }

  isExistingMultiVinOrMultiStockNumberSearch(existingSearchRequest) {
    return existingSearchRequest?.vinList?.length > 0 || existingSearchRequest?.stockList?.length > 0;
  }

  private clearParams() {
    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: {}
    });
  }

  private getDefaultFromDate() {
    let defaultFromDate = undefined;
    if (this.filterByStatus === StatusFilter.SOLD_VEHICLES) {
      defaultFromDate = this.dateUtilsService.DEFAULT_DATES.today;
    } else if (this.filterByStatus === StatusFilter.REMOVED_VEHICLES) {
      defaultFromDate = this.dateUtilsService.DEFAULT_DATES.removedFromDate;
    } else if (this.filterByStatus === StatusFilter.ALL_VEHICLES) {
      defaultFromDate = this.dateUtilsService.DEFAULT_DATES.allVehiclesFromDate;
    }
    return defaultFromDate;
  }

  private filterByStatusShared() {
    // Shared List Search does not use a filter by status value
    if (this.filterByStatus === 'SHARED') {
      return null;
    }
    return this.filterByStatus === StatusFilter.ALL_VEHICLES ? null : this.filterByStatus;
  }

  executeDefaultSearch() {
    const currentPageInStore = this.store.selectSnapshot(SellConfigurationState.returnToInventory);
    this.resetReservedFiltersUI();
    this.store.dispatch(new ResetFilters());
    this.searchFiltersService.setStatusFilter(this.filterByStatus);
    const sort = this.getSortBy();
    const currentRequest = this.store.selectSnapshot(SellSearchState.searchRequest);
    const filterByRepostedVal = !currentRequest.filterByReposted
      ? this.removeFilterByTypes.all
      : (currentRequest.filterByReposted as RemoveFilter);
    const groundedVal = this.getGroundedFlag();
    this.store.dispatch(
      new SearchAuctionItems({
        filterByStatus: this.filterByStatusShared(),
        filterByOfferLists: this.filterByStatus === StatusFilter.SHARED_VEHICLES ? true : undefined,
        filterByReposted: this.filterByStatus === StatusFilter.SHARED_VEHICLES ? null : filterByRepostedVal,
        page: currentPageInStore - 1,
        itemsPerPage: 10,
        ...sort,
        tenantUuid: '',
        fromDate: this.getDefaultFromDate(),
        toDate: this.dateUtilsService.DEFAULT_DATES.toDate,
        leadSellerGroupMembers: this.selectedMemberUuid,
        filterByInService: this.filterByIsv,
        grounded: groundedVal,
        reservedByUsers: this.reservedByUserValue === null ? null : [this.reservedByUserValue],
        reservedForDealers: this.dealershipValue === null ? null : [this.dealershipValue]
      })
    );
    this.setTenantRadioButtonsToAll();
    this.store.dispatch(
      new UpdateReturnToInventory(this.store.selectSnapshot(SellConfigurationState.returnToInventory))
    );
    switch (this.filterByStatus) {
      case StatusFilter.ACTIVE_VEHICLES:
        // Ensure the proper filter is selected when coming from query params
        if (this.searchFiltersService.currentFilter !== 'offers') {
          this.searchFiltersService.selectFilter('offers');
        }
        break;
      case StatusFilter.SOLD_VEHICLES:
        // Ensure the proper filter is selected when coming from query params
        if (this.searchFiltersService.currentFilter !== 'bidsOffers') {
          this.searchFiltersService.selectFilter('bidsOffers');
        }
        break;
      case StatusFilter.SHARED_VEHICLES:
        // Ensure the proper filter is selected when coming from query params
        if (this.searchFiltersService.currentFilter !== 'sharedLists') {
          this.searchFiltersService.selectFilter('sharedLists');
          this.allRecipientSelected = true;
          this.userFilterValue = 'NONE';
        }
        break;
      case StatusFilter.REMOVED_VEHICLES:
        this.filterByRepostedSelected = filterByRepostedVal;
        break;
    }
  }

  getSortBy() {
    switch (this.filterByStatus) {
      case StatusFilter.SOLD_VEHICLES:
        return searchParamsFromSellSort(this.store.selectSnapshot(SellConfigurationState.soldSortConfig)) || {};
      case StatusFilter.HOLD_VEHICLES:
        return searchParamsFromSellSort(this.store.selectSnapshot(SellConfigurationState.holdSortConfig)) || {};
      case StatusFilter.ALL_VEHICLES:
        return searchParamsFromSellSort(this.store.selectSnapshot(SellConfigurationState.allSortConfig)) || {};
      case StatusFilter.REMOVED_VEHICLES:
        return searchParamsFromSellSort(this.store.selectSnapshot(SellConfigurationState.removedSortConfig)) || {};
      case StatusFilter.SHARED_VEHICLES:
        return searchParamsFromSellSort(this.store.selectSnapshot(SellConfigurationState.sharedSortConfig)) || {};
      case StatusFilter.ACTIVE_VEHICLES:
      default:
        return searchParamsFromSellSort(this.store.selectSnapshot(SellConfigurationState.activeSortConfig)) || {};
    }
  }

  setOfferFilterUI(event: Event) {
    event.preventDefault();
    const target = (event.target as unknown) as HTMLInputElement;
    const filterValue = target.value as DealerFilter;
    if (filterValue === this.selectedBidsOffers) {
      return;
    }
    this.bsoUnsavedChangesService.checkForUnsavedChanges(() => {
      this.setOfferFilter(filterValue);
      this.bsoUnsavedChangesService.resetBso();
    });
  }

  handleOfferListRadioButton(
    dealerToRecipientLists: DealerToRecipient[],
    recipientOfferLists: RecipientOfferLists,
    offerListMeta: OfferListMeta,
    buyerDealerName: string
  ) {
    this.store.dispatch(new ClearAllSelectedVehicles());
    this.userFilterValue = 'NONE';

    let offerListUuids = [];
    if (offerListMeta === null) {
      this.allRecipientSelected = true;
      recipientOfferLists = {
        buyerRecipientName: null,
        offerLists: []
      };
      offerListMeta = {
        offerListUuid: null,
        offerListName: null,
        selected: false
      };
      dealerToRecipientLists.forEach(dealerToRecipient => {
        dealerToRecipient.recipientOfferLists.forEach(recipientOfferList => {
          recipientOfferList.offerLists.forEach(offerList => {
            offerList.selected = false;
          });
        });
      });
    } else {
      this.allRecipientSelected = false;
      dealerToRecipientLists.forEach(dealerToRecipient => {
        dealerToRecipient.recipientOfferLists.forEach(recipientOfferList => {
          if (
            dealerToRecipient.buyerDealerName !== buyerDealerName ||
            recipientOfferList.buyerRecipientName !== recipientOfferLists.buyerRecipientName
          ) {
            recipientOfferList.offerLists.forEach(offerList => {
              offerList.selected = false;
            });
          } else {
            recipientOfferLists.offerLists.forEach(offerList => {
              offerList.selected = offerListMeta.offerListUuid === offerList.offerListUuid;
            });
          }
        });
      });
    }

    if (offerListMeta.offerListUuid === null) {
      offerListUuids = null;
    } else if (offerListMeta.offerListName === 'All Lists') {
      recipientOfferLists.offerLists.forEach(offerList => {
        if (
          offerListUuids.find(x => x === offerList.offerListUuid) === undefined &&
          offerList.offerListName !== 'All Lists'
        ) {
          offerListUuids.push(offerList.offerListUuid);
        }
      });
    } else {
      offerListUuids.push(offerListMeta.offerListUuid);
    }

    this.store.dispatch([
      new ClearUserUuids(),
      new SetOfferFilter(DealerFilter.OFFERS_NONE),
      new UpdateFilterStatus(StatusFilter.SHARED_VEHICLES),
      new UpdatedOfferLists(
        offerListUuids,
        buyerDealerName,
        recipientOfferLists.buyerRecipientName,
        offerListMeta.offerListUuid
      ),
      new DoSearch()
    ]);
  }

  setOfferFilter(dealerFilter: DealerFilter) {
    this.store.dispatch([
      new SetOfferFilter(dealerFilter),
      new DoSearch({ resetSelectedOfferDealers: dealerFilter === DealerFilter.OFFERS_BY_DEALER })
    ]);
  }

  setQuestionFilterUI(event: Event) {
    event.preventDefault();
    const target = (event.target as unknown) as HTMLInputElement;
    const filterValue = target.value as DealerFilter;
    if (filterValue === this.selectedBidsOffers) {
      return;
    }
    this.bsoUnsavedChangesService.checkForUnsavedChanges(() => {
      this.setQuestionFilter(filterValue);
      this.bsoUnsavedChangesService.resetBso();
    });
  }

  setQuestionFilter(questionFilter: DealerFilter) {
    this.store.dispatch([
      new SetQuestionFilter(questionFilter),
      new DoSearch({ resetSelectedOfferDealers: questionFilter === DealerFilter.QUESTIONS_BY_DEALER })
    ]);
  }

  setFilterByUserUI(event: Event, type: any) {
    event.preventDefault();
    if (type === this.userFilterValue) {
      return;
    }
    this.userFilterValue = type;
    this.store.dispatch([new ClearAllSelectedVehicles(), new ClearOfferListUuids()]);
    if (type === 'NONE') {
      this.allRecipientSelected = true;
      this.store.dispatch([
        new ClearUserUuids(),
        new SetOfferFilter(DealerFilter.OFFERS_NONE),
        new UpdateFilterStatus(StatusFilter.SHARED_VEHICLES),
        new DoSearch()
      ]);
    } else {
      this.allRecipientSelected = false;
      this.setOfferFilterUI(event);
    }
  }

  setStatusFilterUI(event: Event) {
    this.searchFiltersService.resetPagination.next(true);
    // If ISV vehicle and status is either Active/Reserved then keep grounding status section expanded if collapsed on status selection
    if (this.isGroundingStatusFilterHideShow && !this.groundedSectionOpen) {
      this.groundedSectionOpen = true;
    }
    event.preventDefault();
    const target = (event.target as unknown) as HTMLInputElement;
    const statusFilter = target.value as StatusFilter;
    if (statusFilter === this.filterByStatusSelected) {
      return;
    } else {
      // If we're selecting a new filter, clear out any selected vehicles
      this.store.dispatch(new ClearAllSelectedVehicles());
      this.store.dispatch(new ClearOfferListUuids());
      this.allRecipientSelected = true;
      this.userFilterValue = 'NONE';
    }
    // pushing the current selected status filter to store
    this.store.dispatch(new UpdateFilterStatus(statusFilter));
    this.bsoUnsavedChangesService.checkForUnsavedChanges(() => {
      this.setStatusFilter(statusFilter);
      this.bsoUnsavedChangesService.resetBso();
    });
    if (statusFilter !== StatusFilter.SHARED_VEHICLES) {
      this.store.dispatch([new SetSingleListSelected(false)]);
    }
  }

  dataTrackId(showAll: boolean, dealerCount: number) {
    // string values do not need to be translated, as they are used for analytics only
    return showAll === true ? `Show All ${dealerCount} Dealers` : 'Show Less';
  }

  setStatusFilter(statusFilter: StatusFilter) {
    if (statusFilter === StatusFilter.SOLD_VEHICLES) {
      this.searchFiltersService.selectFilter('bidsOffers');
      this.selectedBidsOffers = undefined;
    }
    if (statusFilter === StatusFilter.ACTIVE_VEHICLES) {
      this.searchFiltersService.selectFilter('offers');
      this.selectedBidsOffers = DealerFilter.OFFERS_NONE;
    }
    if (statusFilter === StatusFilter.SHARED_VEHICLES) {
      this.searchFiltersService.selectFilter('sharedLists');
      this.selectedBidsOffers = DealerFilter.OFFERS_NONE;
    }
    if (statusFilter === StatusFilter.REMOVED_VEHICLES) {
      this.filterByRepostedSelected = this.removeFilterByTypes.all;
    }
    this.store.dispatch([
      new UpdateFilterStatus(statusFilter),
      new SetStatusFilter(statusFilter),
      new SetDefaultSortForStatus(statusFilter),
      new ResetDateFilters(statusFilter),
      new ResetMultiVin(),
      new UpdateLeadSellerPayload(selectedGroupMemberUuids(this.leadSellerGroupService.snapShot())),
      new DoSearch(),
      new CloseDrawer()
    ]);
  }

  setBidFilter(bidFilter: BidFilter) {
    this.store.dispatch([
      new SetBidFilter(bidFilter),
      new ResetMultiVin(),
      new UpdateLeadSellerPayload(selectedGroupMemberUuids(this.leadSellerGroupService.snapShot())),
      new DoSearch(),
      new CloseDrawer()
    ]);
  }

  setTenantFilter(tenantFilter) {
    const currentRequest = this.store.selectSnapshot(SellSearchState.searchRequest);
    const auctionItemTenants: AuctionItemTenant[] = this.store.selectSnapshot(SellSearchState.auctionItemTenants);
    const tenantAuctionItems: AuctionItemTenant = auctionItemTenants.find(auctionItem => {
      return auctionItem.name === tenantFilter ? auctionItem : undefined;
    });
    const tenantUuid: string = tenantAuctionItems?.uuid;
    const payload = {
      ...currentRequest,
      page: 0,
      tenantUuid: tenantUuid ? tenantUuid : ''
    };
    this.store.dispatch([new SetSelectedTenant(tenantFilter), new SearchAuctionItems(payload)]);
    this.tenantSelected = tenantFilter;
  }

  setRepostedFilterUI(event: Event) {
    this.searchFiltersService.resetPagination.next(true);
    event.preventDefault();
    const target = (event.target as unknown) as HTMLInputElement;
    const removeFilter = target.value as RemoveFilter;
    if (removeFilter === this.filterByRepostedSelected) {
      return;
    }
    this.setRemoveFilter(removeFilter);
  }

  setRemoveFilter(removeFilter: RemoveFilter) {
    const currentRequest = this.store.selectSnapshot(SellSearchState.searchRequest);
    const payload = {
      ...currentRequest,
      page: 0,
      filterByReposted: removeFilter === 'ALL' ? '' : removeFilter
    };
    this.store.dispatch(new SearchAuctionItems(payload));
    this.filterByRepostedSelected = removeFilter;
  }

  removedVehiclesSA(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-removalTypeFilterEnabled', false);
  }

  // @TODO: Not sure what this is supposed to be enabling, but we need to change the name or use a different flag..
  isSellRemovalTypeFilterEnabled(): boolean {
    return !this._launchDarklyService.getFeatureFlag('sell-removalTypeFilterEnabled', false);
  }

  setBidsOffersFilter(bidsOffersFilter: DealerFilter) {
    this.store.dispatch([
      new SetDealerFilterSellSearch(bidsOffersFilter),
      new DoSearch({ resetSelectedOfferDealers: bidsOffersFilter === DealerFilter.WON_BY_DEALER })
    ]);
  }

  setWonDealersFilter(bidsOffersFilter: DealerFilter) {
    this.store.dispatch([
      new SetDealerFilterSellSearch(bidsOffersFilter),
      new DoSearch({ resetSelectedOfferDealers: bidsOffersFilter === DealerFilter.WON_BY_DEALER })
    ]);
  }

  setLostDealersFilter(bidsOffersFilter: DealerFilter) {
    this.store.dispatch([
      new SetDealerFilterSellSearch(bidsOffersFilter),
      new DoSearch({ resetSelectedOfferDealers: bidsOffersFilter === DealerFilter.BIDS_OFFERS_BY_DEALER })
    ]);
  }

  toggleSelectAllOffers() {
    this.store.dispatch([new ToggleSelectAllOffers(), new DoSearch()]);
    this.selectAllOffersCheckbox.nativeElement.checked = !this.selectAllOffersCheckbox.nativeElement.checked;
  }

  toggleSelectAllUsers() {
    this.store.dispatch([new ToggleSelectAllUsers(), new DoSearch()]);
    this.selectAllUsersCheckbox.nativeElement.checked = !this.selectAllUsersCheckbox.nativeElement.checked;
    this.analyticsService.fireEventOnCheckboxValueChange('Select All Sellers');
  }

  toggleSelectAllQuestions() {
    this.store.dispatch([new ToggleSelectAllQuestions(), new DoSearch()]);
    this.selectAllQuestionsCheckbox.nativeElement.checked = !this.selectAllQuestionsCheckbox.nativeElement.checked;
  }

  selectDealerOffer(id: string) {
    this.store.dispatch([new ToggleDealerOffers(id), new DoSearch()]);
  }

  selectUser(id: string) {
    this.store.dispatch([new ToggleUsers(id), new DoSearch()]);
    this.analyticsService.fireEventOnCheckboxValueChange('Seller Name');
  }

  selectDealerQuestion(id: string) {
    this.store.dispatch([new ToggleDealerQuestions(id), new DoSearch()]);
  }

  dealerById(index: number, dealer: SelectableDealer) {
    return dealer.organizationEntityUuid;
  }

  dealerListById(index: number, list: DealerToRecipient) {
    return list.buyerDealerName;
  }

  userById(user: SelectableUser) {
    return user.offerListCreatorUuid;
  }

  openDealerShipDropDown(list: DealerToRecipient) {
    if (this.openSharedListDealerShip.some(x => x.buyerDealerName === list.buyerDealerName)) {
      const index = this.openSharedListDealerShip.indexOf(list);
      this.openSharedListDealerShip.splice(index, 1);
    } else {
      this.openSharedListDealerShip.push(list);
    }
  }

  determineIfDealerShipIsOpen(list: DealerToRecipient) {
    return this.openSharedListDealerShip.some(x => x.buyerDealerName === list.buyerDealerName);
  }

  get isShowBuyingDealershipName(): boolean {
    return this._launchDarklyService.getFeatureFlag('showBuyingDealershipName', false);
  }

  loadLeadSellerGroupAndHandleParam() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd && e.url.includes('?')),
        switchMap(() => {
          /*
           Ensure that the lead seller group members have loaded before making the request
           when handling query strings.
           */
          return this.leadSellerRequestMade$.pipe(
            filter(requestMade => requestMade),
            take(1),
            tap(() => {
              this.bsoUnsavedChangesService.checkForUnsavedChanges(
                () => {
                  this.handleParams();
                },
                () => {
                  this.clearParams();
                }
              );
            })
          );
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();
  }

  setTenantRadioButtons() {
    this.store
      .select(SellSearchState.auctionItemTenants)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((auctionItemTenants: AuctionItemTenant[]) => {
        if (auctionItemTenants && auctionItemTenants.length === 0) {
          this.tenants.splice(1);
          return this.tenants;
        }

        this.addTenantRadioButtons(auctionItemTenants);
        this.store
          .select(SearchFiltersState.getSelectedTenant)
          .pipe(take(1))
          .subscribe(tenantFilter => {
            this.tenants.forEach(tenant => {
              tenant.isChecked = tenant.value === tenantFilter;
            });
          });
        return this.tenants;
      });
  }

  addTenantRadioButtons(auctionItemTenants: AuctionItemTenant[]) {
    this.tenants = [];

    auctionItemTenants?.forEach(auctionItemTenant => {
      const tenantRadioObj = {
        id: 'active-tenant-filter',
        class: 'custom-control-input',
        groupName: 'Tenant',
        name: auctionItemTenant.name,
        value: auctionItemTenant.name,
        dataTrackName: 'Active-Enterprise',
        ariaLabel: auctionItemTenant.name,
        isChecked: false
      };
      this.tenants.push(tenantRadioObj);
    });

    this.tenants.sort((a, b) => a.value.localeCompare(b.value));
    // Ensure ALL radio button is first in array
    this.tenants.unshift(this.defaultRadioButton);
  }

  toggleAllWonDealers(ids?: string[]) {
    this.store.dispatch([new ToggleSelectAllWonDealers(ids), new DoSearch()]);
  }

  toggleAllLostDealers(ids?: string[]) {
    this.store.dispatch([new ToggleSelectAllLostDealers(ids), new DoSearch()]);
  }

  selectWonDealer(id: string) {
    this.store.dispatch([new ToggleWonDealer(id), new DoSearch()]);
  }

  selectLostDealer(id: string) {
    this.store.dispatch([new ToggleLostDealer(id), new DoSearch()]);
  }

  selectReservedByUsersDropdown(event) {
    this.reservedByUserValue = event.target?.dataset?.value;
    if (this.reservedByUserValue) {
      this.store.dispatch([
        new UpdateReservedByUsers(this.reservedByUserValue !== 'All' ? this.reservedByUserValue : null),
        new SetReservedByUsers(this.reservedByUserValue !== 'All' ? [this.reservedByUserValue] : null),
        new DoSearch()
      ]);
    }
  }

  selectDealershipDropdown(event) {
    this.dealershipValue = event.target?.dataset?.value;
    if (this.dealershipValue) {
      this.store.dispatch([
        new UpdateReservedForDealers(this.dealershipValue !== 'All' ? this.dealershipValue : null),
        new SetReservedForDealers(this.dealershipValue !== 'All' ? [this.dealershipValue] : null),
        new DoSearch()
      ]);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getListName(offerList: OfferListMeta) {
    if (offerList.offerListUuid === 'ALL') {
      return this.allListTranslate;
    } else {
      return offerList.offerListName;
    }
  }

  updateSharedVehicleFilterTranslations() {
    if (this.isInServiceVehiclesEnabled && !this.filterByIsv) {
      this.searchFilterTranslations.labelSharedVehiclesFilter = this.searchFilterTranslations.customOfferVehicles;
      this.searchFilterTranslations.labelAllOffersTo = this.searchFilterTranslations.labelAllCustomOffersTo;
    } else {
      this.searchFilterTranslations.labelSharedVehiclesFilter = this.searchFilterTranslations.sharedVehicles;
      this.searchFilterTranslations.labelAllOffersTo = this.searchFilterTranslations.labelAllSharedListOffersTo;
    }
  }

  getTranslate() {
    this.translocoService
      .selectTranslateObject('sellModule.manageInventory.searchFilter')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.tenants[0].name = translate.radioBtnGrpTenant.all;
        this.tenants[0].ariaLabel = translate.radioBtnGrpTenant.all;
        this.allListTranslate = translate.labelAllList;
        this.searchFilterTranslations.labelReserveInformation = translate.labelReserveInformation;
        this.searchFilterTranslations.labelAvisRep = translate.labelAvisRep;
        this.searchFilterTranslations.labelDealership = translate.labelDealership;
        this.searchFilterTranslations.labelSoldDate = translate.labelSoldDate;
        this.searchFilterTranslations.sharedVehicles = translate.textSharedVehicles;
        this.searchFilterTranslations.customOfferVehicles = translate.textCustomOfferVehicles;
        this.searchFilterTranslations.labelAllSharedListOffersTo = translate.labelAllSharedListOffersTo;
        this.searchFilterTranslations.labelAllCustomOffersTo = translate.labelAllCustomOffersTo;
        this.updateSharedVehicleFilterTranslations();

        this.bids.forEach(bid => {
          if (bid.value === BidFilter.ALL) {
            bid.name = translate.radioBtnGrpBids.all;
          } else if (bid.value === BidFilter.WITH_BIDS) {
            bid.name = translate.radioBtnGrpBids.activeBids;
          } else if (bid.value === BidFilter.WITHOUT_BIDS) {
            bid.name = translate.radioBtnGrpBids.noBids;
          }
          bid.ariaLabel = bid.name;
        });
      });
  }

  setTenantRadioButtonsToAll() {
    this.tenants?.forEach(tenant => {
      tenant.isChecked = tenant.value === 'ALL';
    });
    this.tenants = cloneDeep(this.tenants);
  }

  selectVehicleType($event: AvisVehicleType) {
    this.filterByIsv = $event != AvisVehicleType.AVIS_DIRECT;
    this.setFilterByInService();
  }

  /* Used to search Avis Direct or In-Service Vehicles */
  setFilterByInService() {
    const currentRequest = this.store.selectSnapshot(SellSearchState.searchRequest);
    currentRequest.filterByInService = this.filterByIsv;
    // Any filters that exist for ISV but shouldn't exist for non-ISV need to be added here.
    if (
      (!this.filterByIsv && this.filterByStatus === StatusFilter.RESERVED_VEHICLES)
      || (this.filterByIsv && this.filterByStatus === StatusFilter.HOLD_VEHICLES)
    ) {
      currentRequest.filterByStatus = StatusFilter.ACTIVE_VEHICLES;
      this.filterByStatusSelected = StatusFilter.ACTIVE_VEHICLES;
    }
    const payload = {
      ...currentRequest,
      page: 0,
      offerLists: null,
      reservedByUsers: null,
      reservedForDealers: null
    };
    this.dealershipValue = null;
    this.updateSharedVehicleFilterTranslations();
    this.searchFiltersService.resetPagination.next(true);
    this.store.dispatch([
      new ResetFilters(),
      new ClearAllSelectedVehicles(),
      new SearchAuctionItems(payload),
      new UpdateReservedByUsers(null),
      new SetReservedByUsers(null),
      new UpdateReservedForDealers(null),
      new SetReservedForDealers(null)
    ]);
  }

  setGroundedStatusFilterUI(event: Event) {
    event.preventDefault();
    const target = (event.target as unknown) as HTMLInputElement;
    const filterValue = target.value as GroundedStatusFilters;
    if (filterValue === this.selectedGroundedOptions) {
      return;
    }
    this.selectedGroundedOptions = filterValue;
    this.bsoUnsavedChangesService.checkForUnsavedChanges(() => {
      this.setGroundedFilter(filterValue);
      this.bsoUnsavedChangesService.resetBso();
    });
  }

  setGroundedFilter(groundedFilter: GroundedStatusFilters) {
    this.store.dispatch([new SetGroundedStatusFilter(groundedFilter), new DoSearch()]);
  }

  private getGroundedFlag() {
    return this.selectedGroundedOptions === GroundedStatusFilters.GROUNDED
      ? true
      : this.selectedGroundedOptions === GroundedStatusFilters.NOT_GROUNDED
      ? false
      : null;
  }

  resetReservedFiltersUI() {
    if (this.store.selectSnapshot(SellConfigurationState.filterStatus) === StatusFilter.ACTIVE_VEHICLES) {
      this.reservedByUserValue = null;
      this.dealershipValue = null;
    }
  }
}
